import { Box, Grid } from '@mui/material';

import {
    MergeTag,
    WhatsAppButton,
    WhatsAppCarouselCard,
    WhatsAppHeaderComponent,
    WhatsAppFooterComponent,
    WhatsAppLTOComponent,
    WhatsAppTemplateType,
    isHeaderComponent,
    isFooterComponent,
    isButtonsComponent,
    isCarouselComponent,
    isLTOComponent,
} from 'domains/content/types';

import { styles } from '../styles';
import { WhatsAppContentStateTypes } from '../whatsAppTypes';

import WhatsAppButtonsSection from './WhatsAppButtonsSection';
import WhatsAppCarouselSection from './WhatsAppCarouselSection';
import WhatsAppEditor from './WhatsAppEditor';
import WhatsAppLTOSection from './WhatsAppLTOSection';
import WhatsAppPreview from './WhatsAppPreview';
import WhatsAppTemplateFooter from './WhatsAppTemplateFooter';
import WhatsAppTemplateHeader from './WhatsAppTemplateHeader';

// ─── Legacy (flag OFF) wrapper ────────────────────────────────────────────────

type LegacyProps = {
    content: string;
    formattedPreviewContent: string;
    setContent: (content: string) => void;
};

export const WhatsAppContentWrapperLegacy = ({ content, formattedPreviewContent, setContent }: LegacyProps) => (
    <Grid container spacing={3} sx={{ flexGrow: 1 }}>
        <Grid item xs={12} md={6}>
            <Box overflow="auto" paddingBottom={2} paddingRight={4} rowGap={3} sx={styles.container}>
                <WhatsAppEditor content={content} setContent={setContent} />
            </Box>
        </Grid>
        <Grid display="flex" justifyContent="center" item xs={12} md={6}>
            <WhatsAppPreview formattedContent={formattedPreviewContent} />
        </Grid>
    </Grid>
);

// ─── New full builder wrapper (flag ON) ───────────────────────────────────────

type FullProps = {
    state: WhatsAppContentStateTypes;
    mergeTags: MergeTag[];
    onSetBodyText: (text: string) => void;
    onSetHeader: (header: WhatsAppHeaderComponent | null) => void;
    onSetFooter: (footer: WhatsAppFooterComponent | null) => void;
    onSetLanguage: (language: string) => void;
    onAddButton: (button: WhatsAppButton) => void;
    onUpdateButton: (index: number, button: WhatsAppButton) => void;
    onRemoveButton: (index: number) => void;
    onReorderButtons: (buttons: WhatsAppButton[]) => void;
    onAddCarouselCard: (card: WhatsAppCarouselCard) => void;
    onUpdateCarouselCard: (index: number, card: WhatsAppCarouselCard) => void;
    onRemoveCarouselCard: (index: number) => void;
    onSetLTO: (lto: WhatsAppLTOComponent['limited_time_offer']) => void;
};

export const WhatsAppContentWrapperFull = ({
    state,
    mergeTags,
    onSetBodyText,
    onSetHeader,
    onSetFooter,
    onSetLanguage,
    onAddButton,
    onUpdateButton,
    onRemoveButton,
    onReorderButtons,
    onAddCarouselCard,
    onUpdateCarouselCard,
    onRemoveCarouselCard,
    onSetLTO,
}: FullProps) => {
    const headerComponent = state.components.find(isHeaderComponent) ?? null;
    const footerComponent = state.components.find(isFooterComponent) ?? null;
    const buttonsComponent = state.components.find(isButtonsComponent);
    const buttons = buttonsComponent?.buttons ?? [];
    const carouselComponent = state.components.find(isCarouselComponent);
    const ltoComponent = state.components.find(isLTOComponent);
    const lto = ltoComponent?.limited_time_offer ?? null;

    const isCarousel = state.templateType === WhatsAppTemplateType.CAROUSEL;
    const isLTO = state.templateType === WhatsAppTemplateType.LTO;
    const isStandard = state.templateType === WhatsAppTemplateType.STANDARD;

    return (
        <Grid container spacing={3} sx={{ flexGrow: 1 }}>
            <Grid item xs={12} md={6}>
                <Box
                    overflow="auto"
                    paddingBottom={2}
                    paddingRight={4}
                    rowGap={3}
                    sx={{ ...styles.container, display: 'flex', flexDirection: 'column', gap: 3 }}
                >
                    {/* Standard template sections */}
                    {isStandard && (
                        <>
                            <WhatsAppTemplateHeader header={headerComponent} onChange={onSetHeader} />
                            <WhatsAppEditor
                                content={
                                    state.components.find((c) => c.type === 'BODY') &&
                                    'text' in state.components.find((c) => c.type === 'BODY')!
                                        ? (state.components.find((c) => c.type === 'BODY') as any).text
                                        : ''
                                }
                                setContent={onSetBodyText}
                            />
                            <WhatsAppTemplateFooter footer={footerComponent} onChange={onSetFooter} />
                            <WhatsAppButtonsSection
                                buttons={buttons}
                                onAdd={onAddButton}
                                onUpdate={onUpdateButton}
                                onRemove={onRemoveButton}
                                onReorder={onReorderButtons}
                            />
                        </>
                    )}

                    {/* Carousel template */}
                    {isCarousel && (
                        <WhatsAppCarouselSection
                            cards={
                                carouselComponent?.cards ?? [
                                    { header_format: 'IMAGE' as any, header_url: '', body_text: '', buttons: [] },
                                ]
                            }
                            onAddCard={onAddCarouselCard}
                            onUpdateCard={onUpdateCarouselCard}
                            onRemoveCard={onRemoveCarouselCard}
                        />
                    )}

                    {/* LTO template */}
                    {isLTO && (
                        <>
                            <WhatsAppLTOSection lto={lto} onChange={onSetLTO} />
                            <WhatsAppEditor
                                content={
                                    state.components.find((c) => c.type === 'BODY') &&
                                    'text' in state.components.find((c) => c.type === 'BODY')!
                                        ? (state.components.find((c) => c.type === 'BODY') as any).text
                                        : ''
                                }
                                setContent={onSetBodyText}
                            />
                            <WhatsAppButtonsSection
                                buttons={buttons}
                                onAdd={onAddButton}
                                onUpdate={onUpdateButton}
                                onRemove={onRemoveButton}
                                onReorder={onReorderButtons}
                            />
                        </>
                    )}
                </Box>
            </Grid>

            <Grid display="flex" justifyContent="center" item xs={12} md={6}>
                <WhatsAppPreview components={state.components} mergeTags={mergeTags} />
            </Grid>
        </Grid>
    );
};

// ─── Default export: legacy wrapper (for backward compat) ─────────────────────

export default WhatsAppContentWrapperLegacy;
